/**
 * Utils for runtime filters for embedding use case
 */
import {
    EmbedContextOptions,
    useEmbedContext,
} from '@thoughtspot/blink-context';
import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { getRuntimeParameterInput } from '@thoughtspot/mfe-utilities';
import { getPayloadRuntimeFilterLimit } from '@thoughtspot/session-service';
import _ from 'lodash';
import qs, { ParsedQs } from 'qs';
import React from 'react';
import {
    FilterTypes,
    RuntimeFilterInput,
    RuntimeParameterInput,
} from '../services/generated/graphql-types';
import { getRuntimeSortInput } from './runtime-sort.util';

const getColumnParam = (index: number) => `col${index}`;
const getOperatorParam = (index: number) => `op${index}`;
const getValueParam = (index: number) => `val${index}`;

// setting a limit so that we don't end up having an infinite loop
const MAX_RUNTIME_FILTERS = 50;

/**
 * Match strings starting with "col" followed by one or more digits
 */
const COL_KEY_IN_RUNTIME_FILTER_REGEX = /^col(\d+)/;

const FILTER_OPERATORS: { [key: string]: FilterTypes } = {
    EQ: FilterTypes.Eq,
    NE: FilterTypes.Ne,
    LT: FilterTypes.Lt,
    LE: FilterTypes.Le,
    GT: FilterTypes.Gt,
    GE: FilterTypes.Ge,
    CONTAINS: FilterTypes.Contains,
    BEGINS_WITH: FilterTypes.BeginsWith,
    ENDS_WITH: FilterTypes.EndsWith,
    BW_INC_MAX: FilterTypes.BwIncMax,
    BW_INC_MIN: FilterTypes.BwIncMin,
    BW_INC: FilterTypes.BwInc,
    BW: FilterTypes.Bw,
    IN: FilterTypes.In,
    NOT_IN: FilterTypes.NotIn,
};

export function getRuntimeFilterInput(
    queryParams: { [key: string]: any },
    maxRuntimeFilters: number = MAX_RUNTIME_FILTERS,
) {
    const START_INDEX = 1;
    const result: RuntimeFilterInput[] = [];

    // Inclusive bound: a limit of N reads col1..colN, i.e. exactly N filters.
    for (let index = START_INDEX; index <= maxRuntimeFilters; index++) {
        if (_.get(queryParams, getColumnParam(index))) {
            const operatorParam: string = _.get(
                queryParams,
                getOperatorParam(index),
            );
            const filterType = FILTER_OPERATORS[_.toUpper(operatorParam)];
            let filterValue = _.get(queryParams, getValueParam(index));
            if (_.isArray(filterValue)) {
                filterValue = filterValue.map((val: any) => _.toString(val));
                if (_.isEmpty(filterValue)) {
                    filterValue = null;
                }
            } else if (!_.isNil(filterValue)) {
                filterValue = _.toString(filterValue);
            }
            if (!_.isNil(filterType))
                result.push({
                    columnName: _.get(queryParams, getColumnParam(index)),
                    filterType,
                    value: filterValue,
                });
        } else {
            break;
        }
    }
    return result;
}

/**
 * Read runtime filters from the URL search string
 */
export function getRuntimeFilter(defaultQueryParams?: {
    [key: string]: string[] | string | ParsedQs | ParsedQs[];
}): RuntimeFilterInput[] {
    const urlSearchParams = _.mapValues(
        getQueryParamsAndQueryString({ includeSearch: true, includeHash: true })
            .queryParamsMultiValue,
        val => (val.length === 1 ? val[0] : val),
    );

    const result: RuntimeFilterInput[] = getRuntimeFilterInput(urlSearchParams);

    if (!_.isEmpty(defaultQueryParams)) {
        const result2 = getRuntimeFilterInput(
            _.mapValues(defaultQueryParams, (val: any) =>
                val.length === 1 ? val[0] : val,
            ),
        );

        // Avoid duplication - check if each entry already exists before adding it
        result2.forEach(result2Entry => {
            if (
                !result.find(
                    resultEntry =>
                        resultEntry.columnName === result2Entry.columnName &&
                        resultEntry.filterType === result2Entry.filterType &&
                        resultEntry.value === result2Entry.value,
                )
            ) {
                result.push(result2Entry);
            }
        });
    }
    return result;
}

/**
 * Create unique key for set of runtime filters
 */
export const getRuntimeFiltersUniqueKey = (
    runtimeFilters: RuntimeFilterInput[],
) => {
    const filterKeys = _.map(runtimeFilters, runtimeFilter => {
        const { columnName, filterType, value } = runtimeFilter;
        let updatedValues = value;
        if (_.isArray(value)) {
            updatedValues = value.sort();
        }
        return `col:${columnName},op:${filterType},val:${updatedValues}`;
    });
    return _.join(filterKeys, ', ');
};

export const getRuntimeParametersUniqueKey = (
    runtimeParameters: RuntimeParameterInput[],
) => {
    return _.join(
        runtimeParameters
            .filter(p => p.parameterName != null && p.overrideValue != null)
            .map(p => `param:${p.parameterName},val:${p.overrideValue}`),
        ', ',
    );
};

/**
 * Returns a sanitized map of runtime filters
 */
export function getRuntimeFilterInputForDownload(queryParams: {
    [key: string]: any;
}): { [key: string]: string[] } {
    const START_INDEX = 1;
    const result: any = {};

    // Inclusive bound: matches getRuntimeFilterInput so the limit is exactly N.
    for (let index = START_INDEX; index <= MAX_RUNTIME_FILTERS; index++) {
        if (_.get(queryParams, getColumnParam(index))) {
            const operatorParam: string = _.get(
                queryParams,
                getOperatorParam(index),
            );
            const filterType = FILTER_OPERATORS[_.toUpper(operatorParam)];
            let filterValue = _.get(queryParams, getValueParam(index));
            if (_.isArray(filterValue)) {
                filterValue = filterValue.map((val: any) => _.toString(val));
                if (_.isEmpty(filterValue)) {
                    filterValue = null;
                }
            } else if (!_.isNil(filterValue)) {
                filterValue = [_.toString(filterValue)];
            }
            if (!_.isNil(filterType)) {
                result[getColumnParam(index)] = [
                    _.get(queryParams, getColumnParam(index)),
                ];
                result[getOperatorParam(index)] = [filterType];
                result[getValueParam(index)] = filterValue;
            }
        } else {
            break;
        }
    }
    return result;
}

/**
 * Removes runtime filter keys from urlParams
 * @param urlParams urlParams
 * @param onlyRemoveIfValueIsEmpty Should remove only if value is empty or should remove anyways
 */
const removeRuntimeFiltersByKey = (
    urlParams: { [key: string]: string[] },
    onlyRemoveIfValueIsEmpty: boolean,
): { [key: string]: string[] } => {
    const urlParamsCopy = { ...urlParams };

    Object.keys(urlParamsCopy).forEach((key: string) => {
        const runtimeFilterCheck = key.match(COL_KEY_IN_RUNTIME_FILTER_REGEX);
        if (runtimeFilterCheck) {
            // Get numerical suffix for this col key
            const idx = (runtimeFilterCheck[1] as unknown) as number;
            if (
                !onlyRemoveIfValueIsEmpty ||
                (onlyRemoveIfValueIsEmpty &&
                    !_.get(urlParamsCopy, getValueParam(idx))?.length)
            ) {
                [key, getOperatorParam(idx), getValueParam(idx)].forEach(
                    k => delete urlParamsCopy[k],
                );
            }
        }
    });

    return urlParamsCopy;
};

/**
 * Removes colX-opX keys if valX key is either null or its value is empty
 * X being the runtime filter key's numerical suffix
 */
export const removeEmptyValueRuntimeFilters = (urlParams: {
    [key: string]: string[];
}) => {
    return removeRuntimeFiltersByKey(urlParams, true);
};

/**
 * Returns sanitized map of URL parameters for export request
 * removing all the existing runtime filters if there is any embed
 * runtime filter applied
 */
export const getDownloadUrlParameters = (runtimeFilterInput: {
    [key: string]: string[];
}) => {
    const { queryParamsMultiValue } = getQueryParamsAndQueryString({
        includeSearch: true,
        includeHash: true,
    });

    // Sanitize queryParamsMultiValue for runtime filter empty val keys
    let urlParamsCopy = removeRuntimeFiltersByKey(
        queryParamsMultiValue,
        // Remove all existing runtime filters to override embed runtime filters
        _.isEmpty(runtimeFilterInput),
    );
    if (!_.isEmpty(runtimeFilterInput)) {
        const runtimeFilterCopy = removeEmptyValueRuntimeFilters(
            runtimeFilterInput,
        );
        // Merge URL params and embed runtime filters
        urlParamsCopy = {
            ...runtimeFilterCopy,
            ...urlParamsCopy,
        };
    }
    return urlParamsCopy;
};

export const getRuntimeParams = (
    embedConfig: EmbedContextOptions,
    printUrlParams?: {
        [key: string]: string[] | string | ParsedQs | ParsedQs[];
    },
) => {
    const {
        isEmbedded,
        embedParams,
        runtimeFilterParams,
        embedParameterParams,
    } = embedConfig;
    let runtimeFilter = [];
    let runtimeParameters = [];
    let runtimeSort = [];
    if (isEmbedded) {
        const embedRuntimeFilter = !_.isEmpty(runtimeFilterParams)
            ? runtimeFilterParams
            : embedParams;
        const embedRuntimeParameter = !_.isEmpty(embedParameterParams)
            ? embedParameterParams
            : embedParams;
        // Payload runtime filters use the configurable limit; the
        // URL path below stays on the default MAX_RUNTIME_FILTERS.
        runtimeFilter = getRuntimeFilterInput(
            embedRuntimeFilter,
            getPayloadRuntimeFilterLimit(),
        );
        runtimeParameters = getRuntimeParameterInput(embedRuntimeParameter);
        runtimeSort = getRuntimeSortInput(embedParams);
    } else {
        const urlParams =
            printUrlParams &&
            _.mapValues(printUrlParams, (val: any) =>
                val.length === 1 ? val[0] : val,
            );
        runtimeFilter = getRuntimeFilter(printUrlParams);
        runtimeParameters = getRuntimeParameterInput(urlParams);
        runtimeSort = getRuntimeSortInput(urlParams);
    }
    return {
        runtimeFilter,
        runtimeParameters,
        runtimeSort,
    };
};

/*
 * Provides the set of runtime overrides (runtime filters, runtime parameters, runtime sort)
 * currently in effect. Consolidates overrides provided in the URL flags, and in the TSE embed
 * config. You can optionally provide an override set of parsed query params, if you don't want
 * them to be read directly from the page URL.
 */
export const useRuntimeParams = (parsedQueryStringOverride?: ParsedQs) => {
    const embedConfig = useEmbedContext();

    // If the caller gave us parsed query params, then use that directly. If not, obtain and parse
    // the query params from the URL.
    const queryString = parsedQueryStringOverride
        ? null
        : getQueryParamsAndQueryString().queryString;
    const parsedQueryString = React.useMemo(
        () =>
            parsedQueryStringOverride ??
            qs.parse(queryString, { ignoreQueryPrefix: true }),
        [parsedQueryStringOverride, queryString],
    );

    // We need a reference to the current value while generating a potential new value, so we wrap
    // this memo with a ref
    const currentRuntimeParamsRef = React.useRef<
        ReturnType<typeof getRuntimeParams>
    >();
    const currentRuntimeParams: ReturnType<typeof getRuntimeParams> = React.useMemo(() => {
        const {
            runtimeFilter = [],
            runtimeParameters = [],
            runtimeSort = [],
        } = getRuntimeParams(embedConfig, parsedQueryStringOverride);

        // Check which sub-properties have actually changed
        const isFiltersUnchanged = _.isEqual(
            runtimeFilter,
            currentRuntimeParamsRef.current?.runtimeFilter,
        );
        const isParametersUnchanged = _.isEqual(
            runtimeParameters,
            currentRuntimeParamsRef.current?.runtimeParameters,
        );
        const isSortUnchanged = _.isEqual(
            runtimeSort,
            currentRuntimeParamsRef.current?.runtimeSort,
        );

        // Special case where nothing changed - don't need to do anything
        if (isFiltersUnchanged && isParametersUnchanged && isSortUnchanged) {
            return currentRuntimeParamsRef.current;
        }

        // At least one sub-property changed, so we create a new top-level object but avoid
        // recreating any unchanged sub-properties
        return {
            runtimeFilter: isFiltersUnchanged
                ? currentRuntimeParamsRef.current.runtimeFilter
                : runtimeFilter,
            runtimeParameters: isParametersUnchanged
                ? currentRuntimeParamsRef.current.runtimeParameters
                : runtimeParameters,
            runtimeSort: isSortUnchanged
                ? currentRuntimeParamsRef.current.runtimeSort
                : runtimeSort,
        };
    }, [embedConfig, parsedQueryString]);
    currentRuntimeParamsRef.current = currentRuntimeParams;

    return currentRuntimeParams;
};

/**
 * Gets runtime filters based on embed configuration priority.
 * Prioritizes runtimeFilterParams over embedParams, with fallback to getRuntimeFilter().
 * Used by PinboardAttributeFilterContainer to handle excludeRuntimeFiltersFromURL flag.
 */
export const getRuntimeFilters = (embedConfig: any): any[] => {
    const { embedParams, runtimeFilterParams } = embedConfig;

    // Check if runtimeFilterParams has actual filter data (not just empty object)
    const hasRuntimeFilterData =
        runtimeFilterParams && Object.keys(runtimeFilterParams).length > 0;

    const payloadRuntimeFilterLimit = getPayloadRuntimeFilterLimit();
    const embedFilter = hasRuntimeFilterData
        ? getRuntimeFilterInput(runtimeFilterParams, payloadRuntimeFilterLimit)
        : getRuntimeFilterInput(embedParams, payloadRuntimeFilterLimit);
    return embedFilter.length > 0 ? embedFilter : getRuntimeFilter();
};
